// src/components/custom-widgets/CustomWidgets.tsx
import React, { useEffect, useState } from "react";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import styles from "./CustomWidgets.module.scss";
import BabyWeekImages from "../../features/baby-week-images/BabyWeekImages";
import {
  APP_MODULE_ID_TO_KEY,
  CHILDLIVINGSTATUSATBIRTH_STATUS_ID_MAPPING,
  DEFALUT_WIDGET_ORDER,
  WidgetKey,
} from "../../utility/constants/common-constants";
import { useAppSelector } from "../../store/hooks";

// Reusable widget components
import BabySectionGrid from "./widget-grids/BabySectionGrid";
import CarePlanGrid from "./widget-grids/CarePlanGrid";
import MyRewardsGrid from "./widget-grids/MyRewardsGrid";
import MyLearningGrid from "./widget-grids/MyLearningGrid";
import SpotlightSectionGrid from "./widget-grids/SpotlightSectionGrid";
import { useGetDragDropListQuery } from "../../components/custom-drag-drop/services/GetDragDropListSlice";
import FooterComponent from "../../components/footer-component/FooterComponent";

const CustomWidgets: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isBabyWeekImagesOpen, setIsBabyWeekImagesOpen] = useState(false);
  const { livingStatusAtBirthId, actualDeliveryDate } = useAppSelector(
    (s) => s.patientOnboardingStatus.loginWorkflowInfo
  );
  const shouldShowBabyWidgets =
    livingStatusAtBirthId ===
      CHILDLIVINGSTATUSATBIRTH_STATUS_ID_MAPPING.living ||
    livingStatusAtBirthId === null;

  const isBabyDelivered =
    livingStatusAtBirthId ===
      CHILDLIVINGSTATUSATBIRTH_STATUS_ID_MAPPING.living &&
    actualDeliveryDate !== null;

  const {
    patientProfileInfo: { patientProfileId },
  } = useAppSelector((s) => s.patientProfileDetails);
  const preferredLanguage = useAppSelector(
    (s) => s.patientOnboardingStatus.preferredLanguage
  );
  const isSpolightVisible = useAppSelector(
    (s) => s.patientOnboardingStatus.isSpolightVisible
  );

  const handleViewAllBabyImages = () => setIsBabyWeekImagesOpen(true);

  const { data: sortedAppModules = [] } = useGetDragDropListQuery(
    { patientId: patientProfileId, preferredLanguage },
    {
      selectFromResult: ({ data, ...rest }) => ({
        data: data?.appModules
          ? [...data.appModules].sort(
              (a, b) => a.displaySequence - b.displaySequence
            )
          : [],
        ...rest,
      }),
    }
  );

  // Build the map of widgetKey → component
  const widgetMap: Record<WidgetKey, JSX.Element | null> = {
    spotlightSection: <SpotlightSectionGrid />,
    babySection: shouldShowBabyWidgets ? (
      <BabySectionGrid
        onViewAll={handleViewAllBabyImages}
        isBabyDelivered={isBabyDelivered}
      />
    ) : null,
    carePlan: shouldShowBabyWidgets ? <CarePlanGrid /> : null,
    myRewards: <MyRewardsGrid />,
    myLearning: shouldShowBabyWidgets ? <MyLearningGrid /> : null,
  };

  // Determine order from server or fallback
  const sortedWidgets: WidgetKey[] =
    sortedAppModules.length > 0
      ? sortedAppModules
          .filter((mod) => mod.isActive)
          .map((mod) => APP_MODULE_ID_TO_KEY[mod.appModuleId])
          .filter(Boolean)
      : DEFALUT_WIDGET_ORDER;

  // Always force spotlight first
  const renderOrder: WidgetKey[] = ["spotlightSection", ...sortedWidgets];

  // Split into groups for top, middle, bottom wrappers
  const topKeys = renderOrder.slice(0, 2);
  const middleKeys = renderOrder.slice(2, renderOrder.length - 1);
  const bottomKey = renderOrder[renderOrder.length - 1] as WidgetKey;

  // Figure out how many actual widgets in the top section
  const activeTopKeys = topKeys.filter((key) => widgetMap[key] !== null);
  const isSingleTop = activeTopKeys.length === 1;

  // Check positions
  const isRewardAtTop = topKeys[1] === "myRewards";
  const isLearningAtTop = topKeys[1] === "myLearning";
  const isRewardAtBottom = bottomKey === "myRewards";
  const isBabyAtBottom = bottomKey === "babySection";

  // Flags for conditional styling/rendering
  const hasBabyInMiddle = middleKeys.includes("babySection");
  const hasBottomSeparately = renderOrder.length > topKeys.length;

  useEffect(() => {
    if (localStorage.getItem("hideNoshow") === "true") {
      localStorage.removeItem("hideNoshow");
    }
  }, []);

  const zIndexMap: Record<WidgetKey, number> = {
    carePlan: 50,
    spotlightSection: 40,
    babySection: 20,
    myLearning: 30,
    myRewards: 10,
  };

  return (
    <Box className={styles.mainLayout}>
      {/* Baby Week Images Modal */}
      {isBabyWeekImagesOpen && (
        <Box className={styles.fullscreenBabyWeekImage}>
          <BabyWeekImages
            open={isBabyWeekImagesOpen}
            onClose={() => setIsBabyWeekImagesOpen(false)}
          />
        </Box>
      )}

      <Stack spacing={0} sx={{ width: "100%" }}>
        {/* Top wrapper: first one or two widgets */}
        {isSpolightVisible && (
          <Box
            className={[
              topKeys.includes("carePlan")
                ? styles.topBgWrapperCarePlan
                : styles.topBgWrapper,
              (isRewardAtTop || isLearningAtTop) && styles.topReward,
              isSingleTop && styles.topBgWrapperSingle,
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {activeTopKeys.map((key) => (
              <Box
                key={key}
                sx={{
                  width: "100%",
                  position: "relative",
                  zIndex: zIndexMap[key],
                }}
              >
                {widgetMap[key]!}
              </Box>
            ))}
          </Box>
        )}

        {/* Middle wrapper */}
        <Box
          className={
            hasBabyInMiddle
              ? `${styles.middleBgWrapper} ${styles.middleWithBabySection}`
              : styles.middleBgWrapper
          }
        >
          {shouldShowBabyWidgets ? (
            middleKeys.map((key) =>
              widgetMap[key] ? (
                <Box
                  key={key}
                  sx={{
                    width: "100%",
                    position: "relative",
                    zIndex: zIndexMap[key],
                  }}
                >
                  {widgetMap[key]!}
                </Box>
              ) : null
            )
          ) : (
            <Box
              sx={{
                width: "100%",
                position: "relative",
                zIndex: zIndexMap.myRewards,
              }}
            >
              <MyRewardsGrid />
              <FooterComponent
                sx={{
                  height: { xs: "200px", sm: "200px", md: "auto" },
                }}
              />
            </Box>
          )}
        </Box>

        {/* Bottom wrapper: only when baby widgets are shown */}
        {shouldShowBabyWidgets &&
          hasBottomSeparately &&
          widgetMap[bottomKey] && (
            <Box
              key={bottomKey}
              className={
                bottomKey !== "carePlan"
                  ? [
                      styles.bottomBgWrapper,
                      isRewardAtBottom && isMobile && styles.bottomRewardMobile,
                      isBabyAtBottom && isMobile && styles.bottomBabyMobile,
                      isBabyAtBottom &&
                        isMobile &&
                        isBabyDelivered &&
                        styles.isOnlyBabyAtBottom,
                    ]
                      .filter(Boolean)
                      .join(" ")
                  : styles.bottomCarePlan
              }
              sx={{
                position: "relative",
                zIndex: zIndexMap[bottomKey],
              }}
            >
              {widgetMap[bottomKey]!}
              <FooterComponent
                sx={{
                  height: { md: "auto" },
                }}
              />
            </Box>
          )}
      </Stack>
    </Box>
  );
};

export default CustomWidgets;
